import React, { useEffect, useState, useMemo } from 'react';
import { GeneralModal } from 'ui/GeneralModal';
import { ImagesCarousel } from 'ui/ImagesCarousel';
import { CloseXComponent as CloseXIcon } from 'ui/Icons';
import { FluidButton } from 'ui/FluidButton';
import { useZipDownload } from './hooks/useZipDownload';

export interface IMediaCarouselImage {
  displayName: string;
  url: string;
  vimeoVideoId?: string;
  uuid?: string; // Required for bulk download feature
}

export interface IMediaCarouselModalProps {
  isOpen: boolean;
  onClose: () => void;
  titleForModal: string;
  images: IMediaCarouselImage[];
  initialSlide: number;
  /** Enable bulk download mode with checkboxes and download buttons */
  enableBulkDownload?: boolean;
}

export const MediaCarouselModal = (props: IMediaCarouselModalProps) => {
  const { enableBulkDownload = false } = props;

  // Selection state for bulk download
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());

  // Zip download mutation
  const zipDownloadMutation = useZipDownload();

  // Reset selection when modal opens/closes
  useEffect(() => {
    if (!props.isOpen) {
      setSelectedIndices(new Set());
    }
  }, [props.isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && props.isOpen) {
        props.onClose();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [props.isOpen, props.onClose]);

  // Get downloadable images (non-videos with UUIDs)
  const downloadableImages = useMemo(() => {
    return props.images.filter(img => !img.vimeoVideoId && !!img.uuid);
  }, [props.images]);

  // Get indices of downloadable images
  const downloadableIndices = useMemo(() => {
    return props.images.map((img, idx) => (!img.vimeoVideoId && !!img.uuid ? idx : -1)).filter(idx => idx !== -1);
  }, [props.images]);

  // Check if all downloadable images are selected
  const allSelected = useMemo(() => {
    if (downloadableIndices.length === 0) return false;
    return downloadableIndices.every(idx => selectedIndices.has(idx));
  }, [downloadableIndices, selectedIndices]);

  // Count of selected images
  const selectedCount = selectedIndices.size;

  // Check if there are any downloadable images
  const hasDownloadableImages = downloadableImages.length > 0;

  // Handler for Select All / Unselect All
  const handleSelectToggle = () => {
    if (allSelected) {
      // Unselect all
      setSelectedIndices(new Set());
    } else {
      // Select all downloadable images
      setSelectedIndices(new Set(downloadableIndices));
    }
  };

  // Handler for download button
  const handleDownload = () => {
    const uuidsToDownload = props.images
      .filter((img, idx) => selectedIndices.has(idx) && !img.vimeoVideoId && img.uuid)
      .map(img => img.uuid!);

    if (uuidsToDownload.length > 0) {
      zipDownloadMutation.mutate(uuidsToDownload);
    }
  };

  return (
    <GeneralModal
      shouldCloseByClickingOutside={true}
      isCloseButtonVisible={false}
      isOpen={props.isOpen}
      onClose={props.onClose}
      modalOverlayClassName="!bg-black-transparent"
      modalWindowClassName={`
w-[800px]
text-black font-pt-sans
mx-5 mb-10 pb-[15px]
md:mx-0 md:mt-0
`}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-noe-display text-brown-prime p-0 m-[20px]">{props.titleForModal}</h3>
        <div
          className="modal-close-button w-40px h-40px p-0 m-[20px] flex justify-center items-center cursor-pointer"
          onClick={props.onClose}
        >
          <CloseXIcon />
        </div>
      </div>
      <ImagesCarousel
        images={props.images}
        showArrows={true}
        showThumbnails={true}
        carouselImageClassName="h-[500px]"
        thumbnailsCarouselClassName="h-[70px]"
        thumbnailsCarouselWrapperClassName="px-[15px]"
        initialSlide={props.initialSlide}
        enableBulkDownload={enableBulkDownload}
        selectedIndices={selectedIndices}
        onSelectionChange={setSelectedIndices}
        isDownloading={zipDownloadMutation.isPending}
      />

      {/* Bulk download buttons - only show when enabled and there are downloadable images */}
      {enableBulkDownload && hasDownloadableImages && (
        <div className="flex justify-between px-[15px] mt-[15px]">
          <FluidButton
            type="secondary"
            onClick={handleSelectToggle}
            fixedWidth="auto"
            textClassName="whitespace-nowrap"
          >
            {allSelected ? 'Unselect All' : 'Select All'}
          </FluidButton>
          <FluidButton
            type="secondary"
            disabled={selectedCount === 0}
            isLoading={zipDownloadMutation.isPending}
            onClick={handleDownload}
            textClassName="whitespace-nowrap"
            fixedWidth="auto"
          >
            Download Selection{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </FluidButton>
        </div>
      )}
    </GeneralModal>
  );
};
